import { Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import formatDate from "date-fns/format";
import { useEffect, useMemo, useState } from "react";
import { mutate } from "swr";

import { checkJudgingKey, exchangeSingleUseAuthCode } from "../../../../api";
import { MainButton, MainButtons } from "../../../../components/button";
import CompetitionHeaderLayout from "../../../../components/CompetitionHeaderLayout";
import useAsyncMemo from "../../../../hooks/useAsyncMemo";
import useCompetition from "../../../../hooks/useCompetition";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import getCompetitionJudgingClosed from "../../../../utils/getCompetitionJudgingClosed";
import Panel from "../Panel";
import { Heading, Intro } from "../Typography";
import { getIsLoggedIn } from "../../../../services/auth";
import { removeAuthCodeFromUrl } from "../hooks/useAuthWall";

const InitialJudgeScreen = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { competition } = useCompetition(params.competitionUUID);

  useEffect(() => {
    // After the user has logged in, the competition will be refreshed.
    // Send the user straight to the comp page if they are fully ready to judge.
    if (
      competition?.role === "MASTER" ||
      competition?.role === "STAFF_JUDGE" ||
      competition?.role === "JUDGE"
    ) {
      navigate(`/${competition.uuid}`);
    }
  }, [competition?.role, competition?.uuid, navigate]);

  const [searchParams] = useSearchParams();

  const [localStorageJudgingKey, setJudgingKey] = useLocalStorage(
    `${params.competitionUUID}/judgingKey`,
    ""
  );

  const [isWaitingForLogin, setIsWaitingForLogin] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const authCode = queryParams.get("authCode");
    if (authCode) {
      const currentUrl =
        queryParams.get("authCodeUrl") ||
        removeAuthCodeFromUrl(window.location.toString());
      setIsWaitingForLogin(true);
      exchangeSingleUseAuthCode(authCode, currentUrl)
        .then(() => {
          mutate("/profiles/me");
          mutate(`/competitions/${params.competitionUUID}`);
          setIsWaitingForLogin(false);
        })
        .catch(() => {
          setIsWaitingForLogin(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const judgingKey = searchParams.get("key") || localStorageJudgingKey;

  const [judgingKeyOK] = useAsyncMemo(async () => {
    if (params.competitionUUID && competition) {
      return await checkJudgingKey({
        competitionUUID: params.competitionUUID,
        judgingKey: judgingKey || "",
      });
    }
    return undefined;
  }, [judgingKey, params.competitionUUID, competition]);

  const isJudgingClosed =
    competition && getCompetitionJudgingClosed(competition);

  const canJudge =
    (!getIsLoggedIn() || competition?.canJudge) && !isJudgingClosed;
  const competitionEndStr = useMemo(
    () =>
      competition &&
      formatDate(new Date(competition.endTime), "do MMMM yyyy 'at' HH:mm"),
    [competition]
  );

  let intro;

  if (canJudge) {
    intro = <Intro>You’ve been asked to judge this competition.</Intro>;
  } else {
    if (!judgingKey) {
      intro = (
        <Intro className="text-danger">
          To judge this competition, please scan the QR code provided by the
          competition organizer.
        </Intro>
      );
    } else if (isJudgingClosed) {
      intro = (
        <Intro className="text-danger">
          <strong>Competition ended</strong>
          <br />
          This competition ended on {competitionEndStr}. Please contact the
          event organiser for any queries.
        </Intro>
      );
    } else if (judgingKeyOK === false) {
      intro = (
        <Intro className="text-danger">
          The QR code you scanned is not valid for this competition.
        </Intro>
      );
    } else if (competition?.judgeRequestPending) {
      intro = (
        <Intro>
          We’re just waiting for the staff to approve your judging request.{" "}
          <br />
          We’ll send you an email once they’ve confirmed you. Please check back
          later!
        </Intro>
      );
    }
  }

  return (
    <CompetitionHeaderLayout>
      <Panel>
        <Heading>
          {competition ? `Welcome to ${competition.name}` : <br />}
        </Heading>
        {!competition || judgingKeyOK === undefined || isWaitingForLogin ? (
          <div className="d-flex justify-content-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {intro}
            {canJudge ? (
              <MainButtons>
                <MainButton
                  onClick={() => {
                    setJudgingKey(judgingKey || "");
                    navigate("./onboarding");
                  }}
                >
                  Get started
                </MainButton>
              </MainButtons>
            ) : null}
          </>
        )}
      </Panel>
    </CompetitionHeaderLayout>
  );
};

export default InitialJudgeScreen;
